import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs';
import {PlatformService} from './platform.service';
import {BotDetectionService} from './bot-detection.service';
import {isPixelSafeReferrer, isPixelSafeUrl} from './pixel-url-safety';
import {isNative} from '../utils';
import {environment} from '../../../environments/environment';

const TIKTOK_PIXEL_ID = 'CV6FNKBC77UC60PKJ3K0';
const META_PIXEL_ID = '1208759880669398';

interface ScriptOptions {
  src?: string;
  inline?: string;
  async?: boolean;
  attrs?: Record<string, string>;
}

/**
 * Meta, TikTok and Google Ads pixels, on the web only (Piet's call: the native apps send
 * Amplitude and nothing else, and there is no ATT prompt).
 *
 * Each SDK's queue stub is defined in `init()`, so a conversion that fires before the SDKs load
 * (the Purchase on a Stripe return fires while the success page is constructed) is queued as
 * before. The SDK scripts themselves load at the first page that is safe for them to see (see
 * `pixel-url-safety.ts`), and once the visitor is known not to be a bot. Every SDK's own page-view
 * tracking is off; `trackPageView` sends one per safe navigation instead.
 */
@Injectable({
  providedIn: 'root'
})
export class TrackingPixelService {
  private readonly document = inject(DOCUMENT);
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly botDetectionService = inject(BotDetectionService);
  private initialized = false;
  private isBot: boolean | undefined;
  private scriptsLoaded = false;
  private pageViewPending = false;

  init(): void {
    if (this.initialized || !this.platformService.isBrowser || isNative || !this.document?.head || !environment.enableTrackingPixels) {
      return;
    }

    this.initialized = true;
    this.defineTikTokStub();
    this.defineGoogleTagStub();
    this.defineMetaStub();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe(() => {
      if (this.isBot === undefined) {
        this.pageViewPending = true;
      } else {
        this.trackPageView();
      }
    });

    // Fails open: the bot check is a heuristic, and an error in it shouldn't cost real visitors'
    // attribution.
    this.botDetectionService.isBot().catch(() => false).then(isBot => {
      this.isBot = isBot;
      if (this.pageViewPending) {
        this.trackPageView();
      }
    });
  }

  /**
   * Whether events may be sent to the pixels now: a web build with pixels on, not a known bot, and
   * a current URL and referrer the pixels may see. The SDKs attach both to every event themselves.
   */
  pixelsActive(): boolean {
    return this.initialized && this.isBot !== true && this.currentLocationIsSafe();
  }

  private currentLocationIsSafe(): boolean {
    const location = this.document.location;
    return isPixelSafeUrl(location.href) && isPixelSafeReferrer(this.document.referrer, location.origin);
  }

  private trackPageView(): void {
    if (this.isBot !== false || !this.currentLocationIsSafe()) {
      return;
    }

    if (!this.scriptsLoaded) {
      this.scriptsLoaded = true;
      this.loadScripts();
    }

    const {origin, pathname, search} = this.document.location;
    this.call('fbq', 'track', 'PageView');
    // The fragment is the only part of a safe URL that isn't vetted for content, so leave it out.
    this.call('gtag', 'event', 'page_view', {send_to: environment.googleAdsId, page_location: `${origin}${pathname}${search}`});
    this.call('ttq.page');
  }

  private loadScripts(): void {
    this.call('ttq.load', TIKTOK_PIXEL_ID, {historyObserver: false});
    this.appendScript({src: `https://www.googletagmanager.com/gtag/js?id=${environment.googleAdsId}`, async: true});
    this.appendScript({src: 'https://connect.facebook.net/en_US/fbevents.js', async: true});
  }

  /** Calls a pixel's global function or `ttq` method, if it is defined. */
  private call(name: 'fbq' | 'gtag' | 'ttq.page' | 'ttq.load', ...args: unknown[]): void {
    const [global, method] = name.split('.');
    const target: unknown = Reflect.get(window, global);
    const fn: unknown = method === undefined ? target : typeof target === 'object' && target !== null ? Reflect.get(target, method) : undefined;
    if (typeof fn === 'function') {
      Reflect.apply(fn, method === undefined ? window : target, args);
    }
  }

  private appendScript(options: ScriptOptions): void {
    const script = this.document.createElement('script');

    if (options.src) {
      script.src = options.src;
      script.async = options.async ?? false;
    }

    if (options.inline) {
      script.textContent = options.inline;
    }

    if (options.attrs) {
      Object.entries(options.attrs).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    this.document.head.appendChild(script);
  }

  // TikTok's base code without its `ttq.load(…)` and `ttq.page()` calls: `loadScripts` loads it,
  // and `trackPageView` sends the page views.
  //
  // `historyObserver: false` (in `loadScripts`) is meant to stop TikTok counting every SPA URL
  // change as a page view, which it does by default. It is the name Tealium's TikTok tag gives
  // that switch; TikTok's own developer docs couldn't be reached to confirm that `ttq.load`
  // reads it. Confirm with TikTok Pixel Helper on the deployed site.
  private defineTikTokStub(): void {
    this.appendScript({
      inline: `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;
          var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=d.createElement("script");
          n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=d.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
        }(window, document, 'ttq');
      `
    });
  }

  // `send_page_view: false`: `trackPageView` sends each page view with a vetted `page_location`.
  private defineGoogleTagStub(): void {
    this.appendScript({
      inline: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${environment.googleAdsId}', {send_page_view: false});
      `
    });
  }

  // Meta's base code without its script tag (`loadScripts` adds it) or its PageView.
  // - `disablePushState`: no automatic PageView on history changes (Meta's SPA guide).
  // - `allowDuplicatePageViews`: without it the SDK drops every PageView after the first, which
  //   is every manual one in an SPA. Not in Meta's docs.
  // - `autoConfig` off: no automatic events (SubscribedButtonClick) or page metadata scraping.
  //   Automatic advanced matching is an Events Manager setting, not this one.
  private defineMetaStub(): void {
    this.appendScript({
      inline: `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[]}(window, document,'script');
        fbq.disablePushState = true;
        fbq.allowDuplicatePageViews = true;
        fbq('set', 'autoConfig', false, '${META_PIXEL_ID}');
        fbq('init', '${META_PIXEL_ID}');
      `
    });
  }
}
